import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useVentureDeals } from '@/hooks/useVentureDeals';
import { supabase } from '@/integrations/supabase/client';

interface VentureDealCreationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const VentureDealCreationModal: React.FC<VentureDealCreationModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { createDeal } = useVentureDeals();
  const [companies, setCompanies] = useState<{ profile_id: string; name: string; sport: string }[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deal_value: '',
    deal_type: '',
    status: 'pending',
    priority: 'medium',
    talent_profile_id: '',
    estimated_completion_date: '',
  });
  
  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    (async () => {
      try {
        setCompaniesLoading(true);
        const { data, error } = await supabase
          .from('talents')
          .select('profile_id, name, sport')
          .limit(100);
        if (error) {
          console.error('VentureDealCreationModal: talents fetch error', error);
          if (isMounted) setCompanies([]);
        } else {
          if (isMounted) setCompanies(data || []);
        }
      } catch (e) {
        console.error('VentureDealCreationModal: talents fetch exception', e);
        if (isMounted) setCompanies([]);
      } finally {
        if (isMounted) setCompaniesLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [open]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createDeal({
        ...formData,
        talent_profile_id: formData.talent_profile_id,
        deal_value: parseFloat(formData.deal_value) || 0,
        deal_category: 'investment'
      });
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        deal_value: '',
        deal_type: '',
        status: 'pending',
        priority: 'medium',
        talent_profile_id: '',
        estimated_completion_date: '',
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating deal:', error);
    } finally {
      setLoading(false);
    }
  };

  const dealTypes = [
    'Equity Investment',
    'Convertible Note',
    'SAFE',
    'Follow-on',
    'Seed Round',
    'Series A',
    'Series B',
    'Bridge Financing'
  ];

  const statusOptions = [
    'pending',
    'term sheet',
    'due diligence',
    'legal review',
    'active',
    'closed',
    'cancelled'
  ];

  const priorityOptions = [
    'low',
    'medium',
    'high',
    'urgent'
  ];

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Investment Deal</DialogTitle>
          <DialogDescription>
            Create a new investment deal with a portfolio company
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Deal Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Series A Investment in TechCorp"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="talent">Portfolio Company</Label>
              <Select
                value={formData.talent_profile_id}
                onValueChange={(value) => setFormData({ ...formData, talent_profile_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companiesLoading ? (
                    <SelectItem disabled value="loading">Loading athletes…</SelectItem>
                  ) : companies.length ? (
                    companies.map((c) => (
                      <SelectItem key={c.profile_id} value={c.profile_id}>
                        {c.name} - {c.sport}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem disabled value="none">No athletes found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Deal Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the investment terms, structure, and key details..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deal_value">Investment Amount ($)</Label>
              <Input
                id="deal_value"
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                placeholder="e.g., 1000000"
                min="0"
                step="1000"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deal_type">Investment Type</Label>
              <Select
                value={formData.deal_type}
                onValueChange={(value) => setFormData({ ...formData, deal_type: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select investment type" />
                </SelectTrigger>
                <SelectContent>
                  {dealTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="completion_date">Target Close Date</Label>
              <Input
                id="completion_date"
                type="date"
                value={formData.estimated_completion_date}
                onChange={(e) => setFormData({ ...formData, estimated_completion_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Investment Deal'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};